<template>
  <div class="page-revision">
    <base-timeline></base-timeline>

    <base-section class="no-padding-bottom" v-if="reservationSaved">
      <div class="container">
        <p class="subhead subhead-light subhead-caps text-center">{{$t('views.confirmation.header.subhead')}}</p>
        <h2 class="title title-bigger title-semi text-center">{{$t('views.confirmation.header.title')}}</h2>
        <hr class="title-separator title-separator-center">
        <p class="text text-bigger text-center">{{$t('views.confirmation.header.text')}}</p>
        <base-button class="base-button-pri base-button-center base-button-margin-top" @click="newReservation">{{$t('buttons.new_reservation')}}</base-button>
      </div>
    </base-section>

    <base-section class="no-padding-bottom" v-if="reservationError">
      <base-card class="base-card-error">
        <div class="container">
          <h2 class="title title-bigger title-semi text-center">{{$t('errors.error')}}</h2>
          <hr class="title-separator title-separator-center">
          <p class="text text-bigger text-center">{{ reservationErrorMessage }}</p>
          <base-button class="base-button-pri base-button-center base-button-margin-top" @click="newReservation">{{$t('buttons.new_reservation')}}</base-button>
        </div>
      </base-card>
    </base-section>

    <base-section class="no-padding-bottom">
      <div class="container">
        <h2 class="title title-bigger title-semi text-center" v-if="!reservationSaved">{{$t('views.revision.title')}}</h2>

        <div class="flights-details">
          <div class="title-border">
            <div class="title-border title-border-grey">
              <h3 class="title title-small title-semi">{{$t('booking.flight_itinerary')}}</h3>
            </div>
          </div>

          <div class="flight-details-items">
            <base-card
              class="base-card-review"
              :class="{ 'base-card-1-2': activeReturnFlight }"
              v-if="activeDepartureFlight && departureDate"
            >
              <p class="text text-small text-semi">{{$t('booking.departure_flight')}}</p>
              <p class="text text-small text-clear"><span class="text text-small text-semi">{{$t('booking.departure')}} :</span> {{ activeDepartureFlight.origin }} | {{ formattedDepartureDate }} {{ formatTime( activeDepartureFlight.departure_time ) }}</p>
              <p class="text text-small"><span class="text text-small text-semi text-clear">{{$t('booking.arrival')}} :</span> {{ activeDepartureFlight.destination }} | {{ formattedDepartureDate }} {{ formatTime( activeDepartureFlight.arrival_time ) }}</p>
              &nbsp;
              <p class="text text-small text-semi">{{$t('booking.boarding_address')}}</p>
              <p class="text text-small text-clear">{{ activeDepartureFlight.boarding_name }}</p>
              <p class="text text-small text-clear">{{ activeDepartureFlight.boarding_address }}</p>
              <p class="text text-small text-clear">{{ getBoardingNoteContent( activeDepartureFlight.origin ) }}</p>
              <p class="text text-small text-clear">{{ getParkingContent( activeDepartureFlight.origin ) }}</p>
            </base-card>

            <base-card class="base-card-review base-card-1-2" v-if="activeReturnFlight && returnDate">
              <p class="text text-small text-semi">{{$t('booking.return_flight')}}</p>
              <p class="text text-small text-clear"><span class="text text-small text-semi">{{$t('booking.departure')}} :</span> {{ activeReturnFlight.origin }} | {{ formattedReturnDate }} {{ formatTime( activeReturnFlight.departure_time ) }}</p>
              <p class="text text-small"><span class="text text-small text-semi text-clear">{{$t('booking.arrival')}} :</span> {{ activeReturnFlight.destination }} | {{ formattedReturnDate }} {{ formatTime( activeReturnFlight.arrival_time ) }}</p>
              &nbsp;
              <p class="text text-small text-semi">{{$t('booking.boarding_address')}}</p>
              <p class="text text-small text-clear">{{ activeReturnFlight.boarding_name }}</p>
              <p class="text text-small text-clear">{{ activeReturnFlight.boarding_address }}</p>
              <p class="text text-small text-clear">{{ getBoardingNoteContent( activeReturnFlight.origin ) }}</p>
              <p class="text text-small text-clear">{{ getParkingContent( activeReturnFlight.origin ) }}</p>
            </base-card>
          </div>
        </div>

        <div class="passenger-details">
          <div class="title-border title-border-grey">
            <h3 class="title title-small title-semi">{{$t('booking.passengers')}}</h3>
          </div>

          <div class="passenger-details-items">
            <base-card
              class="base-card-review"
              :class="{ 'base-card-1-2': passengers.length > 1 }"
              v-if="passengers.length"
              v-for="( passenger, index ) in passengers" :key="index"
            >
              <p class="text text-small text-semi">{{$t('booking.passenger')}} # {{ index + 1 }}</p>
              <p class="text text-small"><span class="text text-small text-semi">{{$t('booking.name')}} :</span> {{ passenger.first_name }} {{ passenger.last_name }}</p>
              <p class="text text-small"><span class="text text-small text-semi">{{$t('booking.employee_number')}} :</span> {{ passenger.employee_number }}</p>
              <p class="text text-small"><span class="text text-small text-semi">{{$t('booking.email')}} :</span> {{ passenger.email }}</p>
              <p v-if="passenger.email_copy" class="text text-small"><span class="text text-small text-semi">{{$t('booking.email_copy')}} :</span> {{ passenger.email_copy }}</p>
              <p class="text text-small"><span class="text text-small text-semi">{{$t('booking.phone')}} :</span> {{ passenger.phone }}</p>
              <p class="text text-small"><span class="text text-small text-semi">{{$t('booking.sex')}} :</span> {{ getSexTranslation( passenger.sex ) }}</p>
              <p class="text text-small"><span class="text text-small text-semi">{{$t('booking.passenger_weight')}} :</span> {{ passenger.passenger_weight }} lbs</p>
              <p class="text text-small"><span class="text text-small text-semi">{{$t('booking.baggage_weight')}} :</span> {{ passenger.baggage_weight }} lbs</p>
              <p class="text text-small text-clear"><span class="text text-small text-semi text-clear">{{$t('booking.additional_info')}} :</span> {{ passenger.additional_info }}</p>
            </base-card>
          </div>
        </div>
      </div>
    </base-section>

    <base-section v-if="!reservationSaved">
      <div class="container">
        <div class="footer-buttons">
          <base-button class="base-button-outline" @click="goPrevious">{{$t('buttons.prev')}}</base-button>
          <base-button class="base-button-pri" @click="confirmReservation">{{$t('buttons.confirm')}}</base-button>
        </div>
      </div>
    </base-section>

    <base-section v-if="reservationSaved">
      <div class="container">
        <base-card class="base-card-sec">
          <h2 class="title title-big title-caps title-light title-white">{{$t('views.confirmation.footer.title')}}</h2>

          <p class="text text-bigger text-white">{{$t('views.confirmation.footer.text')}}</p>

          <p class="text text-bigger text-white">{{$t('views.confirmation.footer.text_2')}} <a href="mailto:rio-tinto-reservations@maxaviation.com">rio-tinto-reservations@maxaviation.com</a>.</p>

          <base-button class="base-button-outline-white base-button-margin-top" @click="cancelReservation">{{$t('buttons.cancel_reservation')}}</base-button>
        </base-card>
      </div>
    </base-section>
  </div>

  <teleport to=".simply-flights-frontend">
    <base-loading v-if="loading"></base-loading>
  </teleport>
</template>

<script>
import { mapState } from "vuex";
import BaseTimeline from "@/components/partials/BaseTimeline.vue";

export default {
  components: {BaseTimeline},
  data() {
    return {
      reservationSaved: false,
      reservationError: false,
      passengerGroupId: null,
      departureReservationId: null,
      returnReservationId: null
    }
  },
  computed: {
    ...mapState( 'optionsModule', [
      'departureDate',
      'returnDate',
      'tripType',
    ]),
    ...mapState( 'flightsModule', [
      'activeDepartureFlight',
      'activeReturnFlight',
    ]),
    ...mapState( 'passengersModule', [
      'passengers'
    ]),
    ...mapState( 'revisionModule', [
      'loading',
      'error'
    ]),
    reservationErrorMessage() {
      if( this.error === 'no_more_space' ) {
        return this.$t( 'errors.no_more_space' )
      }

      return this.$t( 'errors.reservation_failed' )
    },
    formattedDepartureDate() {
      if( !this.departureDate ) return ''

      let y, m, d

      if (this.departureDate instanceof Date) {
        y = this.departureDate.getFullYear()
        m = this.departureDate.getMonth()
        d = this.departureDate.getDate()
      } else {
        const parts = this.departureDate.split('-').map(Number)
        if (parts.length !== 3) return ''
          ;[y, m, d] = parts
        m = m - 1 // JS months are 0-based
      }

      const date = new Date(y, m, d)

      const locale = this.$i18n.locale === 'fr' ? 'fr-CA' : 'en-CA'

      return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    formattedReturnDate() {
      if (!this.returnDate) return ''

      let y, m, d

      if (this.returnDate instanceof Date) {
        y = this.returnDate.getFullYear()
        m = this.returnDate.getMonth()
        d = this.returnDate.getDate()
      } else {
        const parts = this.returnDate.split('-').map(Number)
        if (parts.length !== 3) return ''
          ;[y, m, d] = parts
        m = m - 1 // JS months are 0-based
      }

      const date = new Date(y, m, d)

      const locale = this.$i18n.locale === 'fr' ? 'fr-CA' : 'en-CA'

      return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  },
  methods: {
    formatTime( time ) {
      if( !time ) return ''
      const [hours, minutes] = time.split( ':' ).map( Number )
      const date = new Date()
      date.setHours( hours, minutes, 0 )
      const locale = this.$i18n.locale === 'en' ? 'en-US' : 'fr-CA'

      return new Intl.DateTimeFormat( locale, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: this.$i18n.locale === 'en'
      }).format( date )
    },
    getSexTranslation( sex ) {
      if( !sex ) return ''
      if( sex === 'Male' ) return this.$t( 'booking.male' )
      if( sex === 'Female' ) return this.$t( 'booking.female' )
    },
    goPrevious() {
      this.$router.push( '/reservation-passengers' )
    },
    cancelReservation() {
      this.$router.push( {
        name: 'reservation-cancellation',
        query: {
          passengerGroupId: this.passengerGroupId || '',
          departureReservationId: this.departureReservationId || '',
          returnReservationId: this.returnReservationId || ''
        }
      })
    },
    async confirmReservation() {
      this.reservationError = false
      const flightReservationData = {
        tripType: this.tripType,
        departureFlight: {
          slotId: this.activeDepartureFlight.slot_id,
          flightDate: this.departureDate,
        },
        returnFlight: this.tripType === 'round-trip' ?
          {
            slotId: this.activeReturnFlight.slot_id,
            flightDate: this.returnDate,
          } : null,
        passengers: this.passengers
      }

      try {
        const result = await this.$store.dispatch( 'revisionModule/createReservation', flightReservationData )

        this.$store.commit( 'timelineModule/SET_STEP', 4 )
        this.reservationSaved = true

        this.$nextTick(() => {
          const el = document.getElementById( 'base-timeline' )
          if( el ) {
            el.scrollIntoView( { behavior: 'smooth' } )
          }
        })

        this.passengerGroupId = result.passengerGroupId
        this.departureReservationId = result.departureReservationId

        if( this.tripType === 'round-trip' ) {
          this.returnReservationId = result.returnReservationId
        }

      } catch( error ) {
        this.reservationError = true
        console.log( error )
      }
    },
    newReservation() {
      this.$store.commit( 'flightsModule/CLEAR_FLIGHTS' )
      this.$store.commit( 'passengersModule/CLEAR_PASSENGERS' )
      this.$router.push( '/reservation-options' )
    },
    getParkingContent( location ) {
      if( location === 'Bagotville' ) {
        return this.$t( 'booking.paid_parking' )
      } else {
        return this.$t( 'booking.parking' )
      }
    },
    getBoardingNoteContent( location ) {
      if( location === 'Bagotville' ) {
        return this.$t( 'booking.boarding_note_bagotville' )
      } else {
        return ''
      }
    }
  },
  mounted() {
    this.$store.commit( 'timelineModule/SET_STEP', 3 )
  }
}
</script>